// ?modal=expense&entry=<bill id> (DD-041, DD-045, TD-077): one bill. Not paid yet, it leads with Mark paid: the amount
// paid (entered here when it varies) and the day. Paid, it shows its details. A quarterly or yearly bill's cash leaves
// once and its cost counts in each of its months. A bill missing its amount opens with what its expense usually comes to.
// finance-real round 8 (slice 9, J41, J44): a paid cost passed along at cost names the pot it comes out of here, which is
// how a cost recorded before pots gets one, once the agency has a pot (slice 12 R3). A charge in a pot says so. Its
// category says "a pot" only when it is the pot, since a bill counts in its expense's pot too (port Q7, R4).
import { ReactNode, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { DayField } from "@/components/ui/DayField";
import { FormField } from "@/components/ui/FormField";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { MissingLine, Required } from "../components/Required";
import { WithoutIva } from "../components/WithoutIva";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { useExpenseEntry, useMarkExpensePaid, usePots, useSetBillPass, useSetBillPot } from "../hooks/useExpenses";
import { useClientContracts, useServiceLines } from "../hooks/usePricing";
import { useSubmitOnce, type SendOnce } from "../hooks/useSubmitOnce";
import { JobPending } from "../jobs/JobParts";
import type { ExpenseEntry, ServiceLine } from "../types";
import { billCheck, entryCadenceText, entryFor, monthsText, spreadBillNote } from "../utils/expenses";
import { dateLabel, labelOf, verbAmount } from "../utils/format";
import { closeUnlessPending } from "../utils/mutations";
import { pairedData } from "../utils/pairedData";
import { passAlongText, passCheck, passForm, passInput, type PassForm } from "../utils/passedCosts";
import { newRequestKey } from "../utils/requestKey";
import { PassAlongField } from "./ExpenseFields";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const clientsWord = ENTITY_CONFIG.client.plural.toLowerCase();
const expensesTab = `Business ${ENTITY_CONFIG.expense.plural.toLowerCase()}`;

const MISSING = { title: "Bill not found", message: `This bill no longer exists: its ${ENTITY_CONFIG.expense.label.toLowerCase()} stopped before the bill was paid.` };

type MarkPaid = ReturnType<typeof useMarkExpensePaid>;

export function ExpenseEntryModal({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const data = pairedData(useExpenseEntry(entryId), useServiceLines(), { subject: "Could not load this bill.", support: "Could not load the service lines." });
  // Owned here: its answer seeds the bill as paid before the modal closes on it, so its own payment, pending or done,
  // keeps Mark paid on screen instead of flashing the paid details (as MarkPaidModal's busy flag).
  const markPaid = useMarkExpensePaid();
  const entry = data.subject;
  if (data.load !== "ready" || !entry || !data.support) {
    return <JobPending title={ENTITY_CONFIG.expense.label} data={data} onClose={onClose} missing={MISSING} />;
  }
  const paying = markPaid.isPending || markPaid.isSuccess;
  return entry.status === "settled" && !paying ? (
    <PaidBill entry={entry} lines={data.support} onClose={onClose} />
  ) : (
    <PayBill entry={entry} markPaid={markPaid} onClose={onClose} />
  );
}

/** How this bill passed along to its client is billed (TD-100, ruling 2026-10-11, answer d): editable until an invoice bills it, paid
 * or not. Its own Save, inside the bill's modal (DD-045), named "Save markup" so it reads apart from the footer's action. The fields sit
 * flat under a section label, as JobBreakdown's. */
function PassAlongBlock({ entry, busy, send }: { entry: ExpenseEntry; busy: boolean; send: SendOnce }) {
  const setPass = useSetBillPass();
  const contractsQuery = useClientContracts(entry.clientId ?? undefined);
  const [form, setForm] = useState<PassForm>(() => passForm(entry.passAlong));
  const pass = entry.passAlong;
  const clientId = entry.clientId;
  if (!pass || !pass.editable || !clientId) return null;
  const check = passCheck(form, clientId);
  const input = passInput(form, clientId);
  const submit = () => {
    const { passBilling, markupBp, markupMinor } = input;
    if (!passBilling) return;
    send(() => setPass.mutateAsync({ id: entry.id, input: { passBilling, markupBp, markupMinor } }));
  };
  // `own` stays false in practice. Naming refuses a client with a cost passed along, and the server refuses passing one to it (TD-105).
  return (
    <div className="space-y-3">
      <div className="text-xs font-medium text-gray-500">Passed along to the {clientWord}</div>
      <PassAlongField clientId={clientId} own={contractsQuery.data?.ownClient ?? false} value={form} onChange={setForm} markupError={check.markupError} required billingLocked={pass.billed} />
      <div className="flex justify-end">
        <Button onClick={submit} disabled={check.blocked || busy || setPass.isPending} isLoading={setPass.isPending}>Save markup</Button>
      </div>
    </div>
  );
}

function PayBill({ entry, markPaid, onClose }: { entry: ExpenseEntry; markPaid: MarkPaid; onClose: () => void }) {
  const [amount, setAmount] = useState<number | null>(entry.missing ? entry.usualMinor : entry.amountMinor);
  const [paidOn, setPaidOn] = useState(() => entry.today);
  // One key per modal open: a double click or a retry of the same payment cannot record it twice.
  const [requestId] = useState(newRequestKey);
  const check = billCheck({ amount, paidOn, pending: markPaid.isPending }, entry.today);
  // What Mark paid waits for, in form order.
  const missing = [...(amount ? [] : ["the amount paid"]), ...(paidOn ? [] : ["the payment date"])];
  const send = useSubmitOnce();
  const submit = () => send(() => markPaid.mutateAsync({ id: entry.id, input: { amountMinor: amount ?? 0, paidOn }, requestId }), () => onClose());
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(markPaid.isPending, onClose)}
      title={`Mark paid · ${entry.label}`}
      footer={
        <>
          <Button onClick={submit} disabled={check.blocked} isLoading={markPaid.isPending}>{verbAmount("Mark paid", amount, entry.currency)}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {entryFor(entry)}, bill dated {dateLabel(entry.date)}.{entry.missing ? " Its amount varies: enter the one on the bill." : ""}
        </p>
        <PassAlongBlock entry={entry} busy={markPaid.isPending} send={send} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label={<Required>Amount paid</Required>} error={amount !== null ? check.amountError : undefined}>
            <MoneyInput valueMinor={amount} currency={entry.currency} onChange={setAmount} invalid={amount !== null && !!check.amountError} ariaLabel="Amount paid" autoFocus required />
            <WithoutIva />
          </FormField>
          <FormField label={<Required>Paid on</Required>} error={check.dateError}>
            {/* finance-real (the user 2026-10-23): a single day is DayField, the calendar every finance date uses. */}
            <DayField ariaLabel="Paid on" required max={entry.today} value={paidOn} onChange={setPaidOn} />
          </FormField>
        </div>
        {(entry.cadence === "quarterly" || entry.cadence === "yearly") && <Callout tone="blue">{spreadBillNote(entry)}</Callout>}
        <MissingLine button="Mark paid" missing={missing} />
      </div>
    </FinanceDrawer>
  );
}

/** Comes out of: the pot a paid cost passed along at cost is a slice of. Its pot's charge paid it, so it moves no cash.
 * Shown on a bill the server says can take one (`canTakePot`), once the agency's pots load and while it has one (R3). */
function PotBlock({ entry }: { entry: ExpenseEntry }) {
  const pots = usePots();
  const setPot = useSetBillPot();
  const [value, setValue] = useState(entry.pot?.id ?? "");
  if (pots.length === 0) return null;
  const changed = value !== (entry.pot?.id ?? "");
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-gray-500">Comes out of</div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <OptionDropdown
            variant="input"
            ariaLabel="Comes out of"
            value={value}
            options={[{ value: "", label: "No pot: BeNerdy paid it on its own" }, ...pots.map((p) => ({ value: p.id, label: p.label }))]}
            onChange={setValue}
            disabled={setPot.isPending}
          />
        </div>
        <Button size="sm" disabled={!changed || setPot.isPending} isLoading={setPot.isPending} onClick={() => setPot.mutate({ id: entry.id, input: { potId: value || null } })}>
          Save pot
        </Button>
      </div>
      <p className="text-xs text-gray-400">
        {value
          ? `The pot's charge already paid it, so it moves no cash of its own. It still goes on the ${clientWord}'s invoice at cost.`
          : "With no pot it is money out on its own, on the day it was paid."}
      </p>
    </div>
  );
}

function PaidBill({ entry, lines, onClose }: { entry: ExpenseEntry; lines: ServiceLine[]; onClose: () => void }) {
  const send = useSubmitOnce();
  const rows: [string, ReactNode][] = [
    [ENTITY_CONFIG.expenseCategory.label, entry.pot && !entry.canTakePot && entry.pot.id === entry.categoryId ? `${entry.categoryLabel} · a pot` : entry.categoryLabel],
    [ENTITY_CONFIG.client.label, entry.clientName ?? "None · overhead"],
    ...(entry.passAlong && !entry.passAlong.editable
      ? [["Passed along", `${passAlongText(entry.passAlong)} · ${formatMoney(entry.passAlong.rebilledMinor, entry.currency)} invoiced`] as [string, ReactNode]]
      : []),
    [ENTITY_CONFIG.serviceLine.label, entry.serviceLineId ? labelOf(lines, entry.serviceLineId) : "·"],
    ["Repeats", entryCadenceText(entry)],
    ["Counts in", monthsText(entry)],
    ["Bill dated", dateLabel(entry.date)],
    ["Paid on", dateLabel(entry.paidOn)],
  ];
  return (
    <FinanceDrawer onClose={onClose} title={entry.label}>
      <div className="space-y-4 text-sm">
        <div className="flex items-center gap-3">
          <Badge tone="green">Paid</Badge>
          <MoneyText amountMinor={-entry.amountMinor} currency={entry.currency} strong />
        </div>
        <div className="rounded-lg bg-gray-50 divide-y divide-gray-100">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="text-gray-500">{label}</span>
              <span className="text-gray-900 text-right">{value}</span>
            </div>
          ))}
        </div>
        <PassAlongBlock entry={entry} busy={false} send={send} />
        {entry.canTakePot && <PotBlock key={entry.pot?.id ?? "none"} entry={entry} />}
        {entry.pot && !entry.canTakePot && <p className="text-xs text-gray-500">{`A charge of the ${entry.pot.label} pot: what ${clientsWord} repay of it is in the pot, on ${expensesTab}.`}</p>}
      </div>
    </FinanceDrawer>
  );
}
